import React, { useCallback, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { CUISINE_CHOICES } from '../../utils/cuisineVocabulary';
import { CANONICAL_DISH_TYPES } from '../../utils/dishTypeVocabulary';
import {
  fieldState,
  liveTimeSuggestion,
  shownTime,
  shownValue,
  suggestedValue,
  visibleChecks,
  type TaggableRecipe,
} from '../../utils/recipeMetadataReview';
import { deriveTimes } from '../../utils/recipeTimeline';
import { formatDuration } from '../../utils/formatDuration';
import { TimeEditor } from './review/TimeEditor';
import {
  DIET_CHOICES,
  REVIEW_FIELDS,
  type FieldState,
  type MetadataDecisions,
  type MetadataField,
  type ReviewField,
  type TimeValue,
} from '../../types/recipeMetadata';

/**
 * The tag strip of a Review row: cuisine, dish type, diet and time, each drawn in
 * the state that says who settled it (FieldState) — you, the AI sure, the AI
 * unsure, nobody, or the retired bulk accept ("auto"). An unsure or missing
 * chip is what keeps Accept locked, so it is the loud one.
 *
 * Tapping a chip opens its list under the strip, with the AI's guess marked:
 * confirming it is one tap, correcting it is one tap. Either saves that field
 * alone, as a person's answer. The time is a number, so its chip opens three
 * fields instead (TimeEditor), pre-filled with the time the chip shows.
 */

const OPTIONS: Record<MetadataField, readonly string[]> = {
  cuisine: CUISINE_CHOICES,
  dishType: CANONICAL_DISH_TYPES,
  diet: DIET_CHOICES,
};

export function valueLabelKey(field: MetadataField, value: string): string {
  if (field === 'cuisine') return `recipeMetadata.cuisines.${value}`;
  if (field === 'diet') return `recipeMetadata.diets.${value}`;
  return `classify.${value}`;
}

/** "45 min", "15 + 30 min", or "15 + 30 · 60 min" when the total is not the sum (resting, chilling). */
export function formatTime(t: (key: string, o?: Record<string, unknown>) => string, time: TimeValue): string {
  if (!time.prep && !time.cook) return t('recipeMetadata.timeTotal', { total: time.total });
  // Hands-on and ready-in alone (a disputed timeline has no cook time of its own).
  if (!time.cook && time.prep !== time.total) return t('recipeMetadata.timePrepTotal', time as unknown as Record<string, unknown>);
  if (time.prep + time.cook === time.total) return t('recipeMetadata.timeParts', time as unknown as Record<string, unknown>);
  return t('recipeMetadata.timePartsTotal', time as unknown as Record<string, unknown>);
}

interface Props {
  recipe: TaggableRecipe & { id: string };
  /** `timeTyped`: the time came from the Time editor, so it may set Préparation (applyTime). */
  onDecide: (decisions: MetadataDecisions, options?: { timeTyped?: boolean }) => Promise<void>;
  /** True while the row's suggestion is being fetched. */
  suggesting?: boolean;
}

export const MetadataSuggestionPanel: React.FC<Props> = ({ recipe, onDecide, suggesting = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<ReviewField | null>(null);
  const [saving, setSaving] = useState<ReviewField | null>(null);
  const suggestion = recipe.metadataSuggestion;

  const decide = useCallback(async (field: ReviewField, decisions: MetadataDecisions, options?: { timeTyped?: boolean }) => {
    setSaving(field);
    try {
      await onDecide(decisions, options);
      setOpen(null);
    } finally {
      setSaving(null);
    }
  }, [onDecide]);

  const openTag = open && open !== 'time' ? open : null;
  const aiGuess = openTag ? suggestedValue(recipe, openTag) : null;
  const timeHint = liveTimeSuggestion(recipe);
  const checks = visibleChecks(recipe);
  const disputed = recipe.timelineStatus === 'review';
  const estimate = disputed && recipe.timelineSuggestion ? deriveTimes(recipe.timelineSuggestion) : null;

  /** The chip's text and its "92% · AI" line, per field. */
  const chipContent = (field: ReviewField): { text: string; meta: string | null } => {
    const state = fieldState(recipe, field);
    const unsettled = state === 'sure' || state === 'unsure';
    if (field === 'time') {
      const time = shownTime(recipe);
      const meta = !unsettled ? null
        // A disputed timeline has no confidence: the checks refused it.
        : timeHint ? `${disputed ? '' : `${timeHint.confidence}% · `}${t(`recipeMetadata.source.${timeHint.source}`)}`
          : time ? t('recipeMetadata.source.publisher') : null;
      return { text: time ? formatTime(t, time) : '', meta };
    }
    const value = shownValue(recipe, field);
    const s = suggestion?.[field];
    return {
      text: value ? t(valueLabelKey(field, value)) : '',
      meta: unsettled && s?.value != null ? `${s.confidence}% · ${t(`recipeMetadata.source.${s.source}`)}` : null,
    };
  };

  return (
    <View style={styles.panel} testID={`metadata-panel-${recipe.id}`}>
      <View style={styles.fields}>
        {REVIEW_FIELDS.map((field) => {
          const state = fieldState(recipe, field);
          const { text, meta } = chipContent(field);
          const look = CHIP_LOOK[state];
          return (
            <View key={field} style={styles.field}>
              <Text style={styles.fieldLabel}>{t(`recipeMetadata.${field}`)}</Text>
              <TouchableOpacity
                testID={`metadata-chip-${field}-${recipe.id}`}
                accessibilityHint={state}
                // @ts-expect-error RNW forwards dataSet as data-* attributes; the QA harness reads data-state.
                dataSet={{ state }}
                style={[styles.chip, look.chip, open === field && styles.chipOpen]}
                onPress={() => setOpen(open === field ? null : field)}
                activeOpacity={0.7}
              >
                {saving === field ? (
                  <ActivityIndicator size="small" color={Paprika.primary} />
                ) : (
                  <>
                    {state === 'confirmed' ? <Feather name="check" size={12} color={look.text} /> : null}
                    {state === 'unsure' ? <Text style={[styles.chipMark, { color: look.text }]}>?</Text> : null}
                    <Text style={[styles.chipText, { color: look.text }]} numberOfLines={1}>
                      {text || (suggesting ? t('recipeMetadata.suggesting') : t('recipeMetadata.choose'))}
                    </Text>
                    {meta ? <Text style={[styles.chipMeta, { color: look.text }]}>{meta}</Text> : null}
                    {state === 'auto' ? (
                      <Text style={[styles.chipMeta, { color: look.text }]}>{t('importReview.auto')}</Text>
                    ) : null}
                    <Feather name={open === field ? 'chevron-up' : 'chevron-down'} size={11} color={look.text} />
                  </>
                )}
              </TouchableOpacity>
            </View>
          );
        })}
      </View>

      {openTag ? (
        <View style={styles.options} testID={`metadata-options-${openTag}-${recipe.id}`}>
          {OPTIONS[openTag].map((value) => {
            const isGuess = value === aiGuess;
            return (
              <TouchableOpacity
                key={value}
                testID={`metadata-option-${openTag}-${value}`}
                style={[styles.option, isGuess && styles.optionGuess]}
                onPress={() => decide(openTag, { [openTag]: value } as MetadataDecisions)}
                disabled={saving !== null}
                activeOpacity={0.7}
              >
                <Text style={[styles.optionText, isGuess && styles.optionTextGuess]}>
                  {t(valueLabelKey(openTag, value))}
                  {isGuess ? ` · ${t('importReview.aiGuess')}` : ''}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : null}

      {open === 'time' ? (
        <TimeEditor
          recipeId={recipe.id}
          initial={shownTime(recipe)}
          hint={timeHint?.value
            ? t(timeHint.source === 'model' ? 'recipeMetadata.timeAiGuess' : 'recipeMetadata.timeFromPage', {
              value: formatTime(t, timeHint.value),
            })
            : null}
          saving={saving === 'time'}
          onSave={(time) => decide('time', { time }, { timeTyped: true })}
        />
      ) : null}

      {checks.length ? (
        <View style={styles.checks}>
          {checks.map((check) => (
            <View key={check.code} style={styles.check} testID={`metadata-check-${check.code}`}>
              <Feather name="alert-triangle" size={11} color={Paprika.approvalIdea} />
              <Text style={styles.checkText} numberOfLines={2}>
                {t(`recipeMetadata.checks.${check.code}`, {
                  items: (check.items ?? []).join(', '),
                  ready: formatDuration(recipe.readyInMinutes ?? 0, t),
                })}
                {check.detail && check.code === 'image_mismatch' ? ` — ${check.detail}` : ''}
                {(check.code === 'timeline_disputed' || check.code === 'timeline_person') && estimate
                  ? ` — ${t('recipeMetadata.checks.timelineDisputedEstimate', {
                    handsOn: formatDuration(estimate.handsOnMinutes, t),
                    ready: formatDuration(estimate.readyInMinutes, t),
                  })}`
                  : ''}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
};

/** One look per state, shared with the legend so the two never drift. */
export const CHIP_LOOK: Record<FieldState, { chip: object; text: string }> = {
  confirmed: { chip: { backgroundColor: Paprika.approvalYesTint }, text: Paprika.approvalYes },
  sure: { chip: { backgroundColor: Paprika.aiSoft }, text: Paprika.ai },
  unsure: {
    chip: { backgroundColor: Paprika.approvalIdeaTint, borderWidth: 1, borderStyle: 'dashed', borderColor: Paprika.approvalIdea },
    text: Paprika.approvalIdea,
  },
  missing: {
    chip: { backgroundColor: Paprika.surfaceLow, borderWidth: 1, borderStyle: 'dashed', borderColor: Paprika.outlineVariant },
    text: Paprika.textMuted,
  },
  auto: { chip: { backgroundColor: Paprika.aiTint }, text: Paprika.textMuted },
};

const styles = StyleSheet.create({
  panel: {
    gap: PaprikaSpacing.xs,
  },
  fields: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.sm,
  },
  field: {
    gap: 2,
  },
  fieldLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minHeight: 30,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: PaprikaRadius.sm,
  },
  chipOpen: {
    backgroundColor: Paprika.brandSoft,
  },
  chipMark: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    lineHeight: 16,
  },
  chipText: {
    ...PaprikaTypography.labelMedium,
    maxWidth: 170,
  },
  chipMeta: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    lineHeight: 14,
    opacity: 0.85,
  },
  options: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    paddingTop: 2,
  },
  option: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.brandTint,
  },
  optionGuess: {
    backgroundColor: Paprika.aiSoft,
  },
  optionText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.primary,
  },
  optionTextGuess: {
    color: Paprika.ai,
  },
  checks: {
    gap: 2,
  },
  check: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.approvalIdeaTint,
    alignSelf: 'flex-start',
  },
  checkText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.approvalIdea,
    flexShrink: 1,
  },
});

export default MetadataSuggestionPanel;
